import { generateDeviceKey, unwrapPrivateKeyWithKey, wrapPrivateKeyWithKey } from './chatCrypto';
import type { KeyWrap } from './chatCrypto';
import type { UserKeys } from './chatService';

/**
 * Trusted-browser record: a non-extractable AES-256-GCM device key in
 * IndexedDB (`studysync-chat-trust` / `devices`, one record per Firebase uid)
 * wrapping a local copy of the private key, so everyday unlock is silent.
 * Never uploaded. Trust lasts 30 days from the last successful unwrap and is
 * renewed by use.
 */

export const TRUST_DB_NAME = 'studysync-chat-trust';
export const TRUST_STORE_NAME = 'devices';
export const TRUST_TTL_MS = 30 * 24 * 60 * 60 * 1000;

export interface TrustedDeviceRecord {
  uid: string;
  deviceKey: CryptoKey;
  wrap: KeyWrap;
  publicKey: string;
  trustEpoch: number;
  /** Epoch milliseconds of the last successful unwrap. */
  lastUnwrapAt: number;
}

export interface DeviceStore {
  get(uid: string): Promise<TrustedDeviceRecord | undefined>;
  put(record: TrustedDeviceRecord): Promise<void>;
  delete(uid: string): Promise<void>;
}

function awaitRequest<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB request failed'));
  });
}

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(TRUST_DB_NAME, 1);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(TRUST_STORE_NAME)) {
        db.createObjectStore(TRUST_STORE_NAME, { keyPath: 'uid' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB open failed'));
    request.onblocked = () => reject(new Error('IndexedDB open blocked'));
  });
}

async function withStore<T>(
  mode: IDBTransactionMode,
  run: (store: IDBObjectStore) => Promise<T>,
): Promise<T> {
  const db = await openDatabase();
  try {
    const transaction = db.transaction(TRUST_STORE_NAME, mode);
    const result = await run(transaction.objectStore(TRUST_STORE_NAME));
    await new Promise<void>((resolve, reject) => {
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error ?? new Error('IndexedDB transaction failed'));
      transaction.onabort = () => reject(transaction.error ?? new Error('IndexedDB transaction aborted'));
    });
    return result;
  } finally {
    db.close();
  }
}

const indexedDbStore: DeviceStore = {
  get: (uid) => withStore('readonly', (store) => awaitRequest(store.get(uid))),
  put: (record) => withStore('readwrite', async (store) => {
    await awaitRequest(store.put(record));
  }),
  delete: (uid) => withStore('readwrite', async (store) => {
    await awaitRequest(store.delete(uid));
  }),
};

/** No storage available (IndexedDB missing or blocked): nothing is ever trusted. */
const nullStore: DeviceStore = {
  get: async () => undefined,
  put: async () => undefined,
  delete: async () => undefined,
};

/** An in-memory store with the same contract, for tests. */
export function createMemoryDeviceStore(): DeviceStore {
  const records = new Map<string, TrustedDeviceRecord>();
  return {
    get: async (uid) => records.get(uid),
    put: async (record) => {
      records.set(record.uid, record);
    },
    delete: async (uid) => {
      records.delete(uid);
    },
  };
}

let storeOverride: DeviceStore | null = null;

export function setDeviceStoreForTests(store: DeviceStore | null): void {
  storeOverride = store;
}

function deviceStore(): DeviceStore {
  if (storeOverride) return storeOverride;
  return typeof indexedDB === 'undefined' ? nullStore : indexedDbStore;
}

async function safeGet(uid: string): Promise<TrustedDeviceRecord | undefined> {
  try {
    return await deviceStore().get(uid);
  } catch {
    return undefined;
  }
}

async function safeDelete(uid: string): Promise<void> {
  try {
    await deviceStore().delete(uid);
  } catch {
    /* storage unavailable — nothing to delete */
  }
}

/** Create (or replace) this browser's record for `uid` after a successful unlock or setup. */
export async function trustBrowser(
  uid: string,
  publicKey: string,
  privateKey: Uint8Array,
  trustEpoch: number,
  now: number = Date.now(),
): Promise<void> {
  const deviceKey = await generateDeviceKey();
  const wrap = await wrapPrivateKeyWithKey(deviceKey, privateKey);
  try {
    await deviceStore().put({ uid, deviceKey, wrap, publicKey, trustEpoch, lastUnwrapAt: now });
  } catch {
    /* storage unavailable (e.g. private mode) — the session still works; the next load asks again */
  }
}

/**
 * The record for `uid`, or null when there is none, it belongs to another uid,
 * or its `lastUnwrapAt` is more than 30 days old (such a record is deleted
 * before use).
 */
export async function readTrustedRecord(
  uid: string,
  now: number = Date.now(),
): Promise<TrustedDeviceRecord | null> {
  const record = await safeGet(uid);
  if (!record || record.uid !== uid) return null;
  if (now - record.lastUnwrapAt > TRUST_TTL_MS) {
    await safeDelete(uid);
    return null;
  }
  return record;
}

export type SilentUnlockResult =
  | { status: 'unlocked'; privateKey: Uint8Array }
  | { status: 'none' }
  | { status: 'invalidated'; recordPublicKey: string };

/**
 * Page-load unlock: only after `GET /keys/me` answered with a `trustEpoch` not
 * above the record's and the same `publicKey` is the private key unwrapped
 * with the device key. A newer epoch or a different key deletes the record.
 */
export async function silentUnlock(
  uid: string,
  keys: UserKeys,
  now: number = Date.now(),
): Promise<SilentUnlockResult> {
  const record = await readTrustedRecord(uid, now);
  if (!record) return { status: 'none' };
  if (keys.trustEpoch > record.trustEpoch || keys.publicKey !== record.publicKey) {
    await safeDelete(uid);
    return { status: 'invalidated', recordPublicKey: record.publicKey };
  }
  return unwrapRecord(record, now);
}

async function unwrapRecord(record: TrustedDeviceRecord, now: number): Promise<SilentUnlockResult> {
  let privateKey: Uint8Array;
  try {
    privateKey = await unwrapPrivateKeyWithKey(record.deviceKey, record.wrap);
  } catch {
    await safeDelete(record.uid);
    return { status: 'none' };
  }
  try {
    await deviceStore().put({ ...record, lastUnwrapAt: now });
  } catch {
    /* renewal is best-effort */
  }
  return { status: 'unlocked', privateKey };
}

/**
 * Return from the idle lock: unwrap again from the device key without calling
 * `GET /keys/me`. The 30-day check still applies and the unwrap refreshes
 * `lastUnwrapAt`. Null means the unlock gate must be shown.
 */
export async function reunlockFromDeviceKey(
  uid: string,
  now: number = Date.now(),
): Promise<Uint8Array | null> {
  const record = await readTrustedRecord(uid, now);
  if (!record) return null;
  const result = await unwrapRecord(record, now);
  return result.status === 'unlocked' ? result.privateKey : null;
}

/** After "Sign out of all other trusted browsers": store the returned epoch so this browser stays trusted. */
export async function setTrustedEpoch(uid: string, trustEpoch: number): Promise<void> {
  const record = await safeGet(uid);
  if (!record || record.uid !== uid) return;
  try {
    await deviceStore().put({ ...record, trustEpoch });
  } catch {
    /* best-effort; the next load compares epochs again */
  }
}

/** Logout / a 404 from `GET /keys/me`: delete the signed-out uid's record only. */
export async function deleteTrustedRecord(uid: string): Promise<void> {
  await safeDelete(uid);
}
